import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

// Module-level cache for verification results
// This survives React 18 Strict Mode unmount/remount cycles
// Keys: token, Values: { status: 'pending' | 'success' | 'error', error?: string, redirectUrl?: string }
const verificationCache = new Map();

const MagicLinkVerify = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const hasCheckedCache = useRef(false);

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid magic link - no token provided");
      return;
    }

    // Check if we already have a cached result for this token
    const cached = verificationCache.get(token);

    if (cached) {
      // If verification is already complete, apply the cached result
      if (cached.status === "success") {
        setStatus("success");
        setTimeout(() => {
          navigate(cached.redirectUrl || "/dashboard", { replace: true });
        }, 1500);
        return;
      } else if (cached.status === "error") {
        setStatus("error");
        setErrorMessage(cached.error || "Failed to verify magic link");
        return;
      }
      // If status is 'pending', another render already started verification
      // Just wait for it to complete and update the cache
      if (cached.status === "pending" && !hasCheckedCache.current) {
        hasCheckedCache.current = true;
        // Poll the cache until the result is ready
        const pollInterval = setInterval(() => {
          const updatedCache = verificationCache.get(token);
          if (updatedCache && updatedCache.status !== "pending") {
            clearInterval(pollInterval);
            if (updatedCache.status === "success") {
              setStatus("success");
              setTimeout(() => {
                navigate(updatedCache.redirectUrl || "/dashboard", { replace: true });
              }, 1500);
            } else {
              setStatus("error");
              setErrorMessage(updatedCache.error || "Failed to verify magic link");
            }
          }
        }, 100);
        // Clean up interval after 30 seconds max
        setTimeout(() => clearInterval(pollInterval), 30000);
        return;
      }
      return;
    }

    // Mark as pending BEFORE making the API call
    verificationCache.set(token, { status: "pending" });

    const verify = async () => {
      try {
        const result = await verifyMagicLink(token);

        if (result.success) {
          // Cache the success result
          verificationCache.set(token, {
            status: "success",
            redirectUrl: result.redirectUrl
          });
          setStatus("success");

          // Redirect after a short delay to show success message
          setTimeout(() => {
            navigate(result.redirectUrl || "/dashboard", { replace: true });
          }, 1500);
        } else {
          // Cache the error result
          verificationCache.set(token, {
            status: "error",
            error: result.error || "Failed to verify magic link"
          });
          setStatus("error");
          setErrorMessage(result.error || "Failed to verify magic link");
        }
      } catch (error) {
        const errMsg = error.response?.data?.error?.message ||
          error.message ||
          "An error occurred while verifying the magic link";
        // Cache the error result
        verificationCache.set(token, {
          status: "error",
          error: errMsg
        });
        setStatus("error");
        setErrorMessage(errMsg);
      }
    };

    verify();
  }, [searchParams, verifyMagicLink, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-xl font-bold">DZ</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {status === "verifying" && "Verifying your magic link..."}
          {status === "success" && "Successfully signed in!"}
          {status === "error" && "Verification failed"}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === "verifying" && (
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-sm text-gray-600">
                Please wait while we verify your magic link...
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <CheckCircleIcon className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Welcome back!
              </h3>
              <p className="text-sm text-gray-600">
                Redirecting you to your dashboard...
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <XCircleIcon className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Verification failed
              </h3>
              <p className="text-sm text-gray-600 mb-6">{errorMessage}</p>

              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  Common issues:
                </p>
                <ul className="text-xs text-gray-500 text-left list-disc list-inside space-y-1">
                  <li>The link may have expired (links are valid for 15 minutes)</li>
                  <li>The link may have already been used</li>
                  <li>The link may be invalid or corrupted</li>
                </ul>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => navigate("/login")}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Return to login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MagicLinkVerify;
